import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BulkEditTokenPost, ProductToken } from '@sleed/bizcommerce-api';
import { productsApi } from '~/lib/api/client';
import { productKeys } from '~/hooks/use-products-list';

/**
 * Products grid mutations.
 *  - remove:   soft-delete via `remove(id)`.
 *  - duplicate: fetch enriched → strip identity (Id/Code) → insert. Best-effort; the
 *    generated client has no dedicated clone endpoint.
 *  - quickEdit / bulkEdit: persist flags via `bulkEditPost`. `quickEdit` is the single-id
 *    side-sheet path; `bulkEdit` is the multi-id mass-edit path. Same endpoint, same payload
 *    type — only the fields the caller sets are applied, everything else is left untouched.
 *  - erpSync: trigger the ERP sync plugin action via `forceERPSync` for a set of product ids
 *    (the endpoint only reads each item's Id — see ProductsController.ForceERPSync).
 *  - create / update: persist a full ProductToken from the editor (`insert` / `update`).
 *    Both invalidate the products cache; the editor re-hydrates from the returned token.
 */
export function useProductMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: productKeys.all });

	const create = useMutation({
		mutationFn: async (token: ProductToken) => (await productsApi.insert(token)).data,
		onSuccess: invalidate
	});

	const update = useMutation({
		mutationFn: async (token: ProductToken) => (await productsApi.update(token)).data,
		onSuccess: invalidate
	});

	const remove = useMutation({
		mutationFn: async (id: number) => (await productsApi.remove(id)).data,
		onSuccess: invalidate
	});

	const duplicate = useMutation({
		mutationFn: async (id: number) => {
			const { data: source } = await productsApi.getByIdEnriched(id);
			const clone: ProductToken = {
				...source,
				id: undefined,
				cd: source.cd ? `${source.cd}-COPY` : ''
			};
			return (await productsApi.insert(clone)).data;
		},
		onSuccess: invalidate
	});

	const quickEdit = useMutation({
		mutationFn: async (payload: BulkEditTokenPost) => (await productsApi.bulkEditPost(payload)).data,
		onSuccess: invalidate
	});

	const bulkEdit = useMutation({
		mutationFn: async (payload: BulkEditTokenPost) => (await productsApi.bulkEditPost(payload)).data,
		onSuccess: invalidate
	});

	const erpSync = useMutation({
		// The endpoint only reads each item's Id (see ProductsController.ForceERPSync); the other
		// required ProductToken fields (tcid/cd/nm) are ignored, so we send minimal placeholders.
		mutationFn: async (ids: number[]) => {
			const items: ProductToken[] = ids.map(id => ({ id, tcid: 0, cd: '', nm: '' }));
			return (await productsApi.forceERPSync({ items })).data;
		},
		onSuccess: invalidate
	});

	return { create, update, remove, duplicate, quickEdit, bulkEdit, erpSync };
}
